import DatasetTable from "@/components/DatasetTable/DatasetTable";
import ImpactPill from "@/components/Impact/ImpactPill";
import styles from "@/components/ImpactReferenceSelector/ImpactReference.module.scss";
import ImpactReferenceModal from "@/components/ImpactReferenceSelector/ImpactReferenceModal";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTag from "@/components/StatusTag/StatusTag";
import { datasetTypeToLabel, ImpactDataset } from "@/hooks/useEcoInventDatasets";
import { DeleteOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Col, Row, Space, Tooltip } from "antd";
import React, { useEffect, useState } from "react";

import useIntercom from "@/hooks/useIntercom";

interface ImpactReferenceLoadingProps {
  data: boolean;
  onDatasetConfirm?: boolean;
}

export interface ImpactReferenceSelectedContentProps {
  type: "material" | "product" | "consumable" | "goods_and_services_item";
  name: string;
  infoMessage?: string;
}

export default function ImpactReferenceSelector({
  selectedDataset,
  onDatasetChange,
  confirmedDataset,
  onDatasetConfirm: parentOnDatasetConfirm,
  onDelete: parentOnDelete,
  showIncomplete,
  modalTitle,
  suggestionTabContent,
  loading,
  activeTab,
  onActiveTabChange,
  selectedContent,
  disabledTooltip,
  defaultDatabaseSearch,
  defaultDatabaseFilter,
  disableIntercomMarginSetter,
  includeExioBase = false,
  defaultTab,
}: {
  selectedDataset: ImpactDataset | undefined;
  onDatasetChange: (dataset?: ImpactDataset) => void;
  confirmedDataset?: ImpactDataset | undefined;
  onDatasetConfirm?: (dataset?: ImpactDataset) => void;
  showIncomplete?: boolean;
  modalTitle?: string;
  onDelete?: () => void;
  suggestionTabContent?: React.ReactNode;
  loading?: ImpactReferenceLoadingProps;
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  selectedContent?: ImpactReferenceSelectedContentProps;
  disabledTooltip?: React.ReactNode;
  defaultDatabaseSearch?: Record<string, any>;
  defaultDatabaseFilter?: Record<string, any>;
  disableIntercomMarginSetter?: boolean;
  includeExioBase?: boolean;
  defaultTab?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const dataIsLoading = loading?.data;
  const onDatasetConfirmIsLoading = loading?.onDatasetConfirm || false;
  const incomplete = showIncomplete && !selectedDataset;
  const { setMarginBottom } = useIntercom();

  useEffect(() => {
    onDatasetChange(confirmedDataset);
  }, [confirmedDataset, isOpen]);

  const onClose = () => {
    setIsOpen(false);
    if (!disableIntercomMarginSetter) {
      setMarginBottom(0);
    }
  };

  const onOpen = () => {
    setIsOpen(true);
    if (!disableIntercomMarginSetter) {
      setMarginBottom(85);
    }
  };

  const onSubmit = async () => {
    await parentOnDatasetConfirm?.(selectedDataset);
    onClose();
  };

  const onDelete = () => {
    parentOnDelete?.();
    onDatasetChange(undefined);
    onClose();
  };

  const onTabChange = (tab: string) => {
    onActiveTabChange?.(tab);
  };

  const emptyDataset = (
    <Row
      className={`${styles["dataset"]} ${incomplete ? styles["incomplete"] : styles["general"]}`}
      align={"middle"}
    >
      <Col
        span={12}
        className={styles["secondary-text"]}
      >
        No impact reference selected
      </Col>
      <Col
        span={12}
        style={{ textAlign: "right" }}
      >
        <Space>
          <Tooltip title={disabledTooltip}>
            <Button
              type={"primary"}
              ghost={true}
              onClick={onOpen}
              icon={<EditOutlined />}
              disabled={!!disabledTooltip}
            >
              Choose impact reference
            </Button>
          </Tooltip>
          <span className={styles.stat}>
            {incomplete && (
              <StatusTag
                status="incomplete"
                showIcon={false}
              />
            )}
          </span>
        </Space>
      </Col>
    </Row>
  );

  const datasetDetail = (
    <Row
      className={styles["dataset"]}
      align={"middle"}
    >
      <Col span={24}>
        <Row gutter={[10, 10]}>
          <Col
            span={24}
            className={styles["primary-text"]}
          >
            {dataIsLoading ? (
              <Row>
                <Col span={20}>
                  <Skeleton style={{ width: "100%", height: 22 }} />
                </Col>
                <Col span={4}>
                  <Skeleton style={{ width: "65px", height: 22, justifySelf: "right" }} />
                </Col>
              </Row>
            ) : (
              <Row align={"middle"}>
                <Col span={20}>{confirmedDataset?.name}</Col>
                <Col
                  span={4}
                  style={{ textAlign: "right" }}
                  className={styles.stat}
                >
                  <StatusTag
                    status="complete"
                    showIcon={false}
                  />
                </Col>
              </Row>
            )}
          </Col>
          <Col
            span={24}
            className={styles["secondary-text"]}
          >
            {dataIsLoading ? (
              <Skeleton style={{ width: 200, height: 22 }} />
            ) : (
              [
                datasetTypeToLabel(confirmedDataset?.dataset_type || "eco_invent") + " reference",
                confirmedDataset?.geography,
              ]
                .filter((x) => x)
                .join(" • ")
            )}
          </Col>
          <Col
            span={24}
            className={styles["impact-footer"]}
          >
            <Row align={"middle"}>
              <Col span={12}>
                <Space>
                  <ImpactPill
                    impact={confirmedDataset?.impact}
                    before={<span className={styles.bold}>1 {confirmedDataset?.unit} impact</span>}
                    loading={dataIsLoading}
                  />
                </Space>
              </Col>
              <Col
                span={12}
                className={styles["right"]}
              >
                <Space>
                  {dataIsLoading ? (
                    <Skeleton style={{ width: 32, height: 32 }} />
                  ) : (
                    <Button
                      danger={true}
                      type={"link"}
                      onClick={onDelete}
                      icon={<DeleteOutlined />}
                      disabled={!!disabledTooltip}
                    >
                      Delete
                    </Button>
                  )}
                  {dataIsLoading ? (
                    <Skeleton style={{ width: 77, height: 32 }} />
                  ) : (
                    <Tooltip title={disabledTooltip}>
                      <Button
                        type={"primary"}
                        ghost={true}
                        onClick={onOpen}
                        icon={<EditOutlined />}
                        disabled={!!disabledTooltip}
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  )}
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const currentSelectionContent = selectedContent && (
    <>
      <div className={styles.currentSelection}>
        <span className={styles.title}>For {selectedContent.type}</span>
        <span className={styles.material}>{selectedContent.name}</span>
        <span
          className={styles.title}
          style={{ marginTop: 4 }}
        >
          Currently saved impact reference
        </span>
        {confirmedDataset ? (
          <DatasetTable
            type="grey"
            data={[confirmedDataset]}
            emptyState={{ noResultsMessage: <span>No reference selected yet.</span> }}
            hideHeader={true}
            hideRaster={true}
            activeRowKey={selectedDataset?.id || confirmedDataset?.id}
            onRowClick={(record) => onDatasetChange(record)}
          />
        ) : (
          <span className={styles.material}>No reference saved yet.</span>
        )}
      </div>
      {selectedContent.infoMessage && (
        <div className={styles["info-message"]}>
          <Space>
            <InfoCircleOutlined />
            <span>{selectedContent.infoMessage}</span>
          </Space>
        </div>
      )}
    </>
  );

  const bottomLeft = confirmedDataset && (
    <Button
      disabled={!confirmedDataset}
      danger
      onClick={onDelete}
    >
      <DeleteOutlined />
    </Button>
  );

  return (
    <>
      {!confirmedDataset && !dataIsLoading ? emptyDataset : datasetDetail}
      {isOpen && (
        <ImpactReferenceModal
          title={modalTitle ? modalTitle : "Add reference"}
          onClose={onClose}
          onSubmit={onSubmit}
          isLoading={onDatasetConfirmIsLoading}
          onDatasetChange={onDatasetChange}
          selectedDataset={selectedDataset}
          defaultDatabaseSearch={defaultDatabaseSearch}
          defaultDatabaseFilter={defaultDatabaseFilter}
          currentSelectionContent={currentSelectionContent}
          suggestionTabContent={suggestionTabContent}
          activeTab={activeTab}
          onTabChange={onTabChange}
          bottomLeft={parentOnDelete && bottomLeft}
          includeExiobase={includeExioBase}
          defaultTab={defaultTab}
        />
      )}
    </>
  );
}
